/**
 * Reading the School Network dashboard's cards.
 *
 * The datahouse writes one "School Network" dashboard per network (P1 Dashboard
 * Data Map, Queries sheet: dashboard_type "School Network", keyed by org_code).
 * It arrives through DashboardContext as { registry, cards }, and every box on
 * the network slides names the card that feeds it — SND.S_3.C_3 is section S_3,
 * card C_3 (Data Map, Boxes sheet).
 *
 * These helpers only READ. They never invent a value: a card that is missing
 * returns null, and each page decides what to show in its place.
 */
import {isSuppressedCell, isSuppressedBenchmark} from '../../utils/suppression';

/** A section by its code, e.g. 'S_3'. */
export const findSection = (dashboardData, sectionCode) =>
    (dashboardData?.cards?.sections || []).find((s) => s.section_code === sectionCode) || null;

/** A card by section and card code, e.g. ('S_3', 'C_3') for SND.S_3.C_3. */
export const findCard = (dashboardData, sectionCode, cardCode) =>
    (findSection(dashboardData, sectionCode)?.cards || []).find((c) => c.card_code === cardCode) || null;

/** Whole number, the way every dashboard shows its figures ("63.69" -> 63); null when there is no value. */
export const whole = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? Math.trunc(n) : null;
};

/**
 * A named benchmark on a bar item, e.g. 'Global avg.'. Cards spell it both
 * "Global avg." and "Global avg" (SND.S_5.C_3 vs SND.S_7.C_3), so the trailing
 * dot and case are ignored. A benchmark under the n floor (suppressed) is null,
 * so the tick is simply not drawn.
 */
const benchKey = (n) => String(n || '').trim().replace(/\.$/, '').toLowerCase();
export const benchmarkOf = (item, name) => {
    const b = (item?.benchmark_values || []).find((x) => benchKey(x?.name) === benchKey(name));
    if (!b || isSuppressedBenchmark(b)) return null;
    return whole(b.value);
};

/** A named extra on a KPI card (kpi_add_infos), matched case-insensitively; null when suppressed. */
export const addInfoOf = (card, name) => {
    const key = String(name).toLowerCase();
    const info = (card?.kpi_add_infos || []).find((i) => String(i?.info_name || '').toLowerCase() === key);
    if (!info || isSuppressedBenchmark(info)) return null;
    const v = info.info_value;
    return v === undefined || v === null ? null : v;
};

/** "ASSESSMENT WINDOW" -> "Assessment Window". */
export const titleCase = (s) => String(s || '')
    .toLowerCase()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());

/**
 * The eight COS readiness areas, in their fixed framework order — the same
 * "Readiness Area 1 … 8" numbering every dashboard shows.
 *
 * Needed because the readiness bar cards arrive SORTED BY SCORE and, unlike the
 * context & support items (sublabel "CA1"…), carry no area number of their own
 * (Data Map, Queries sheet, SND.S_3.C_6). Position in the list is therefore not
 * the area number; the name is.
 *
 * The order is the datahouse's own: its other cards label these same names
 * RA1…RA8 (ND.S_2a.C_3 Readiness Items; the sublabels on the School cards).
 */
export const READINESS_AREA_ORDER = [
    'Youth AI-Digital Activities',
    'Self-Regulated AI-Digital Engagement',
    'Digital Hygiene and Privacy Management',
    'Cyberbullying Management',
    'Content Risk Avoidance',
    'Contact Risk Avoidance',
    'Authentic and Ethical Digital Identity',
    'Critical Information Management',
];

/** 1-based area number for a readiness item, from its sublabel or its name. */
export const readinessAreaNumber = (item) => {
    const fromSub = String(item?.sublabel || '').match(/\d+/);
    if (fromSub) return Number(fromSub[0]);
    const idx = READINESS_AREA_ORDER.findIndex(
        (n) => n.toLowerCase() === String(item?.label || '').trim().toLowerCase(),
    );
    return idx >= 0 ? idx + 1 : null;
};

/**
 * Readiness area of an item, from its code: 'CPCI01.SelDigEng.AoOTAR' belongs to
 * area 'CPCI01.SelDigEng' (Data Map samples, SND.S_4.C_5 vs SND.S_3.C_6).
 */
export const parentCode = (code) => String(code || '').split('.').slice(0, 2).join('.');

/**
 * The cyber-risk list in its fixed order, from the "Select a risk" ListCard
 * (SND.S_7.C_2): risks grouped by family, Any Cyber Risk first. Each risk is
 * numbered by its place in that list (01, 02 …), the numbering the risk pages
 * show. The prevalence cards rank risks by value, so this list is the order.
 */
export const riskList = (listCard) => (listCard?.list_objects || []).map((r, i) => ({
    code: r.code,
    name: r.name,
    family: r.parent_name,
    num: String(i + 1).padStart(2, '0'),
}));

/** "CONTENT RISK · Sexual Content Exposure" -> family and name. */
export const splitFamily = (label) => {
    const parts = String(label || '').split(' · ');
    return parts.length > 1
        ? {family: parts[0].trim(), name: parts.slice(1).join(' · ').trim()}
        : {family: null, name: String(label || '').trim()};
};

/** One column of an ORGCard (the header strip), by its column name. */
export const orgColumn = (orgCard, columnName) => {
    const col = (orgCard?.table_objects || []).find(
        (c) => String(c?.column_name || '').toUpperCase() === String(columnName).toUpperCase(),
    );
    const v = col?.values?.[0];
    return v === undefined || v === null || v === '' ? null : v;
};

const isEmptyValue = (v) => v === undefined || v === null || String(v).trim() === '';

/** A table value as the dashboards show it: whole numbers, text as given, "—" when empty. */
export const cellText = (v) => {
    if (isEmptyValue(v)) return '—';
    if (Array.isArray(v)) return v.join(', ');
    // Separate lists in one cell (Baseline Evidence: readiness / cyber_risk /
    // context) — their items as text, for search; the page draws the groups.
    if (typeof v === 'object') return Object.values(v).flat().filter(Boolean).join(', ') || '—';
    const s = String(v).trim();
    return typeof v === 'number' || /^-?\d+(\.\d+)?$/.test(s) ? String(Math.trunc(Number(v))) : s;
};

/** Same, with a % sign for a "percent" column. */
export const formatCell = (col, v) => {
    const text = cellText(v);
    return col?.column_type === 'percent' && /^-?\d+$/.test(text) ? `${text}%` : text;
};

/**
 * Rows of a TableCard in a page's own row shape (the pages keep their own
 * headers and layout). spec: { rowKey: 'Card column name' | [names] }. A column
 * the card does not send gives "—". Each row also carries `_cell` (cell_data),
 * `_raw` (the value as sent) and `_suppressed` (true where that column's cell is
 * under the n floor — the text is then "—" and the page draws the placeholder
 * with DashboardCommon/SuppressedValue's RowCell; "—" rather than the copy so
 * that search (rowMatches) and filter options (distinct) ignore it).
 */
export const rowsFrom = (card, spec) => {
    const cols = card?.table_objects || [];
    const colFor = (names) => {
        const list = (Array.isArray(names) ? names : [names]).map((n) => String(n).toLowerCase());
        return cols.find((c) => list.includes(String(c.column_name || '').trim().toLowerCase())) || null;
    };
    const mapped = Object.entries(spec).map(([key, names]) => [key, colFor(names)]);
    const count = Math.max(0, ...cols.map((c) => (c.values || []).length));
    return Array.from({length: count}, (_, i) => {
        // _raw: the value as sent, for a cell drawn from its structure (Baseline
        // Evidence's separate lists) — same as SchoolDashboard/schoolData.
        const row = {_cell: {}, _raw: {}, _suppressed: {}};
        mapped.forEach(([key, col]) => {
            const cd = col ? col.cell_data?.[i] : undefined;
            const sup = isSuppressedCell(cd);
            row[key] = col && !sup ? formatCell(col, col.values?.[i]) : '—';
            row._raw[key] = col && !sup ? col.values?.[i] : (col ? null : undefined);
            row._cell[key] = cd;
            row._suppressed[key] = sup;
        });
        return row;
    });
};

/** Distinct values of one row key, for a filter's options. */
export const distinct = (rows, key) => [...new Set(rows.map((r) => r[key]).filter((v) => v && v !== '—'))];

/** A row matches a search when any of its shown values contains the text. */
export const rowMatches = (row, q) => !q || Object.entries(row).some(([k, v]) => !k.startsWith('_') && String(v).toLowerCase().includes(q));
